import styled, { useTheme } from "styled-components";
import { TabList, TabContext, TabPanel } from "@mui/lab";
import { Tab as MuiTab } from "@mui/material";
import { useEffect, useState } from "react";
import { Children } from "react";
import { useMobile } from "../../utils/mobile";

const StyleTabs = styled(TabList)(({ theme, mobile }) => ({
  flex: 1,
  width: mobile ? "100%" : undefined,

  [`&& .MuiButtonBase-root`]: {
    fontSize: mobile ? 14 : 20,
    fontWeight: mobile ? 500 : 600,
    color: theme.color.gray90,
    whiteSpace: "nowarp",
    // mobile uses variant="fullWidth" → each tab is an equal-width third
    maxWidth: mobile ? "none" : 500,
    minWidth: mobile ? 0 : undefined,
    padding: mobile ? "8px 4px" : `12px 5px`,
  },
  [`&& .MuiTabs-flexContainer`]: {
    justifyContent: mobile ? "stretch" : "start",
    gap: mobile ? 0 : 40,
  },
  [`&& .MuiTabs-indicator`]: {
    color: `${theme.color.primary} !important`,
    backgroundColor: `${theme.color.primary} !important`,
    height: 4,
  },
  [`&& .Mui-selected`]: {
    color: `${theme.color.primary} !important`,
  },
}));

const Tab = ({ value, children }) => ({ children });

// `fillHeight` (mobile): the active panel flexes to fill the remaining height
// and becomes a flex column, so the page can keep the tab bar fixed and let the
// panel's own content scroll.
const Tabs = ({ value, tabChange, children, fillHeight }) => {
  const [tabValue, setTabValue] = useState(value);
  const arrayChildren = Children.toArray(children);
  const theme = useTheme();
  const isMobile = useMobile();

  const onTabChange = (event, newValue) => {
    tabChange(newValue);
    setTabValue(newValue);
  };

  useEffect(() => {
    setTabValue(value);
  }, [value]);

  return (
    <TabContext value={tabValue}>
      <div
        style={{
          borderBottom: 1,
          borderBlockEndStyle: "solid",
          borderColor: theme.color.gray30,
          display: "flex",
          justifyContent: "start",
        }}>
        <StyleTabs
          onChange={onTabChange}
          mobile={isMobile}
          variant={isMobile ? "fullWidth" : "standard"}
        >
          {Children.map(arrayChildren, (child, index) => {
            return (
              <MuiTab
                key={index}
                label={child.props.label}
                value={child.props.value}
              />
            );
          })}
        </StyleTabs>
      </div>
      {Children.map(arrayChildren, (child, index) => {
        return (
          <TabPanel
            key={index}
            value={child.props.value}
            sx={{
              // trim the bulky default top padding on phones
              ...(isMobile ? { px: 0, pt: 1.5, pb: 0 } : {}),
              // Only the visible panel flexes/fills; `:not([hidden])` keeps the
              // inactive panels hidden (sx display would otherwise beat [hidden]).
              ...(isMobile && fillHeight
                ? {
                    "&:not([hidden])": {
                      flex: 1,
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                    },
                  }
                : {}),
            }}
          >
            {child.props.children}
          </TabPanel>
        );
      })}
    </TabContext>
  );
};

export default Tabs;
export { Tab };
